import React, { useState, useCallback, useRef, useEffect } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import {
  BtnBar,
  CtaBtn,
  DangerAlert,
  Form,
  FormRow,
  FormItem,
  Input,
  Select,
  Switch,
  SecondaryBtn,
  SuccessAlert,
  Table,
  WaitingAlert,
} from '@tmapy/style-guide';

import type { ExportToolResponse } from '../../openapi/types/ExportTool';
import type { ExportToolsCreateRequest } from '../../openapi/operations/exportToolsCreate';
import { formatsList } from '../../openapi/operations/formatsList';
import { gpTasksList } from '../../openapi/operations/gpTasksList';
import { FormBlock } from '../../app/components/FormBlock';
import { MainContent } from '../../app/components/MainContent';
import { PageTitle } from '../../app/components/PageTitle';
import { QUERY_STATE } from '../../constants';

import type { UseResource } from '../../hooks/useResource';
import { useHistoryAction, useInput, useSelect } from '../../hooks';
import { useCheckbox } from '../../hooks/useCheckbox';
import { getFormValues } from '../../docs/utils/getformvalues';

import { EXPORT_TOOL_FORM_TYPE } from '../constants';
import {
  exportToolMessages,
  exportToolFormMessages,
  exportToolFormBtnMessages,
  exportToolFormFailMessages,
  exportToolFormQueryMessages,
  exportToolFormTitleMessages,
} from './exportToolMessages';

type SelectItem = { value: string; label: string };

type ExportToolFormProps = {
  formType: EXPORT_TOOL_FORM_TYPE;
  resource: UseResource<ExportToolResponse>;
  submitQueryState: QUERY_STATE;
  deleteQueryState: QUERY_STATE;
  onSubmit(value: ExportToolsCreateRequest): void;
  onDelete(): void;
};

export const ExportToolForm: React.FC<ExportToolFormProps> = ({
  formType,
  resource,
  submitQueryState,
  deleteQueryState,
  onSubmit,
  onDelete,
}) => {
  const intl = useIntl();
  const loadQueryState = resource.queryState;
  const isFormValid = useRef(true);

  const [validate, setValidate] = useState(false);
  const [isFormChanged, setFormChanged] = useState(false);

  const setFormInvalid = (message = 'VALUE_IS_NOT_VALID'): string => {
    isFormValid.current = false;
    return message;
  };

  const onInputChangeCallback = useCallback(() => {
    setFormChanged(true);
    setValidate(false);
  }, [setFormChanged, setValidate]);

  // INPUTS
  const prepareFormats = useCallback(async (filter: string) => {
    const response = await formatsList({});
    const results = response.data?.results ?? [];
    return [
      { value: '', label: '' },
      ...results
        .filter((format) => !filter || format.name.toLowerCase().includes(filter.toLowerCase()))
        .map((format) => ({ value: format.url, label: format.name })),
    ];
  }, []);

  const format = useSelect<string, SelectItem>({
    name: 'format',
    label: intl.formatMessage(exportToolMessages.format),
    initialValue: resource.data?.format ?? '',
    onChange: onInputChangeCallback,
    prepareItems: prepareFormats,
    renderValue: (item) => item || '',
    renderItem: (item) => item.label,
    validate,
    validator: (value) =>
      value ? null : setFormInvalid(intl.formatMessage(exportToolFormMessages.formatErrorMessage)),
  });

  const prepareGpTasks = useCallback(async (filter: string) => {
    const response = await gpTasksList({ query: { name__icontains: filter } });
    return [
      { value: '', label: '' },
      ...(response.data?.results ?? []).map((task) => ({
        value: String(task.id),
        label: task.name,
      })),
    ];
  }, []);

  const isPackageRef = useRef(resource.data?.is_package ?? false);

  const gp_task = useSelect<string, SelectItem>({
    name: 'gp_task',
    label: intl.formatMessage(exportToolMessages.gpTask),
    initialValue: resource.data?.gp_task != null ? String(resource.data.gp_task) : '',
    onChange: onInputChangeCallback,
    prepareItems: prepareGpTasks,
    renderValue: (item) => item || '',
    renderItem: (item) => item.label,
    validate,
    validator: (value) =>
      value || isPackageRef.current
        ? null
        : setFormInvalid(intl.formatMessage(exportToolFormMessages.gpTaskErrorMessage)),
  });

  const is_package = useCheckbox({
    name: 'is_package',
    initialValue: resource.data?.is_package ?? false,
    label: intl.formatMessage(exportToolMessages.isPackage),
    onChange: (e: React.SyntheticEvent) => {
      onInputChangeCallback();
      isPackageRef.current = (e.target as HTMLInputElement).checked;
      if (isPackageRef.current) gp_task.setValue('');
    },
  });
  isPackageRef.current = is_package.isChecked;

  const tool_name = useInput({
    name: 'tool_name',
    label: intl.formatMessage(exportToolMessages.toolName),
    initialValue: resource.data?.tool_name ?? '',
    onChange: onInputChangeCallback,
  });

  const is_default = useCheckbox({
    name: 'is_default',
    initialValue: resource.data?.is_default || false,
    label: intl.formatMessage(exportToolMessages.isDefault),
    onChange: onInputChangeCallback,
  });

  // POST
  const formItems = {
    format,
    gp_task,
    tool_name,
    is_default,
    is_package,
  };

  // HANDLERS
  const handleClose = useHistoryAction('vydejExportToolList');
  const handleClear = useCallback(() => resource.refetch(), [resource]);
  const handleCancel = useCallback(() => resource.refetch(), [resource]);

  const handleDelete = async () => {
    if (window.confirm(intl.formatMessage(exportToolFormMessages.exportToolAlertDelete))) {
      onDelete();
    }
  };

  const handleSubmit = useCallback(() => {
    isFormValid.current = true;
    setValidate(true);
  }, [setValidate]);

  const submit = async () => {
    setValidate(false);
    const values = getFormValues(formItems);
    const isPackage = !!values.is_package;
    onSubmit({
      format: values.format,
      gp_task: isPackage ? null : parseInt(values.gp_task, 10),
      tool_name: values.tool_name.trim() || null,
      is_default: !!values.is_default,
      is_package: isPackage,
    });
  };

  const submitRef = useRef(submit);
  submitRef.current = submit;

  useEffect(() => {
    if (validate && isFormValid.current) {
      setFormChanged(false);
      submitRef.current();
    }
  }, [validate, setFormChanged]);

  const parameters = resource.data?.parameters ?? [];

  // RENDER
  return (
    <MainContent>
      <article className='sg-u-vs-4'>
        <PageTitle>
          <FormattedMessage {...exportToolFormTitleMessages[formType]} />
        </PageTitle>
        {loadQueryState === QUERY_STATE.WAITING && (
          <WaitingAlert>
            <FormattedMessage {...exportToolFormQueryMessages.queryStateWaiting} />
          </WaitingAlert>
        )}
        {loadQueryState === QUERY_STATE.FAIL && (
          <DangerAlert>
            <FormattedMessage {...exportToolFormQueryMessages.loadQueryStateFail} />
          </DangerAlert>
        )}
        {(loadQueryState === QUERY_STATE.SUCCESS || formType === EXPORT_TOOL_FORM_TYPE.NEW) && (
          <Form onSubmit={handleSubmit}>
            <div className='sg-u-vs-2'>
              <FormBlock>
                <FormRow>
                  <FormItem {...format}>
                    <Select {...format} />
                  </FormItem>
                </FormRow>
                <FormRow>
                  <FormItem {...is_package}>
                    <Switch {...is_package} />
                  </FormItem>
                </FormRow>
                <FormRow>
                  <FormItem {...gp_task}>
                    <Select {...gp_task} isDisabled={is_package.isChecked} />
                  </FormItem>
                </FormRow>
                <FormRow>
                  <FormItem {...tool_name}>
                    <Input {...tool_name} />
                  </FormItem>
                </FormRow>
                <FormRow>
                  <FormItem {...is_default}>
                    <Switch {...is_default} />
                  </FormItem>
                </FormRow>
              </FormBlock>

              {parameters.length > 0 && (
                <FormBlock>
                  <PageTitle>
                    <FormattedMessage {...exportToolMessages.parameters} />
                  </PageTitle>
                  <Table>
                    <thead>
                      <tr>
                        <th>
                          <FormattedMessage {...exportToolMessages.parameterName} />
                        </th>
                        <th>
                          <FormattedMessage {...exportToolMessages.parameterDataType} />
                        </th>
                        <th>
                          <FormattedMessage {...exportToolMessages.parameterValue} />
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {parameters.map((parameter) => (
                        <tr key={parameter.id}>
                          <td className='sg-u-type-bold'>{parameter.name}</td>
                          <td>{parameter.data_type}</td>
                          <td>{parameter.value}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </FormBlock>
              )}

              {submitQueryState === QUERY_STATE.WAITING && (
                <WaitingAlert>
                  <FormattedMessage {...exportToolFormQueryMessages.queryStateWaiting} />
                </WaitingAlert>
              )}
              {submitQueryState === QUERY_STATE.FAIL && (
                <DangerAlert>
                  <FormattedMessage {...exportToolFormFailMessages[formType]} />
                </DangerAlert>
              )}
              {submitQueryState === QUERY_STATE.SUCCESS && (
                <SuccessAlert>
                  <FormattedMessage {...exportToolFormQueryMessages.submitQueryStateSuccess} />
                </SuccessAlert>
              )}

              {deleteQueryState === QUERY_STATE.WAITING && (
                <WaitingAlert>
                  <FormattedMessage {...exportToolFormQueryMessages.queryStateWaiting} />
                </WaitingAlert>
              )}
              {deleteQueryState === QUERY_STATE.FAIL && (
                <DangerAlert>
                  <FormattedMessage {...exportToolFormQueryMessages.deleteQueryStateFail} />
                </DangerAlert>
              )}

              <BtnBar>
                <>
                  {formType === EXPORT_TOOL_FORM_TYPE.EDIT && (
                    <>
                      {isFormChanged ? (
                        <>
                          <CtaBtn type='submit'>
                            <FormattedMessage {...exportToolFormBtnMessages.save} />
                          </CtaBtn>
                          <SecondaryBtn isDanger onClick={handleCancel}>
                            <FormattedMessage {...exportToolFormBtnMessages.cancel} />
                          </SecondaryBtn>
                        </>
                      ) : (
                        <CtaBtn onClick={handleClose}>
                          <FormattedMessage {...exportToolFormBtnMessages.undo} />
                        </CtaBtn>
                      )}
                      <SecondaryBtn isDanger onClick={handleDelete}>
                        <FormattedMessage {...exportToolFormBtnMessages.delete} />
                      </SecondaryBtn>
                    </>
                  )}
                  {formType === EXPORT_TOOL_FORM_TYPE.NEW && (
                    <>
                      <CtaBtn type='submit'>
                        <FormattedMessage {...exportToolFormBtnMessages.create} />
                      </CtaBtn>
                      <SecondaryBtn isDanger onClick={handleClear}>
                        <FormattedMessage {...exportToolFormBtnMessages.clear} />
                      </SecondaryBtn>
                    </>
                  )}
                </>
              </BtnBar>
            </div>
          </Form>
        )}
      </article>
    </MainContent>
  );
};
